import React from 'react';
import { Gear, Grip, Lock } from '@gravity-ui/icons';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useUniqId } from '../../../../../hooks';
import { createOnKeyDownHandler } from '../../../../../hooks/useActionHandlers/useActionHandlers';
import { Button } from '../../../../Button';
import { Icon } from '../../../../Icon';
import { TreeSelect } from '../../../../TreeSelect/TreeSelect';
import { TreeSelectItem } from '../../../../TreeSelect/TreeSelectItem';
import { ListContainerView } from '../../../../useList';
import { block } from '../../../../utils/cn';
import i18n from './i18n';
import './TableColumnSetup.css';
function identity(value) {
    return value;
}
const b = block('table-column-setup');
const tableColumnSetupCn = b(null);
const controlsCn = b('controls');
const reorderArray = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};
const prepareDndItems = (tableColumnItems) => {
    return tableColumnItems.map((tableColumnItem) => {
        const hasSelectionIcon = tableColumnItem.isRequired === false;
        return Object.assign(Object.assign({}, tableColumnItem), { startSlot: tableColumnItem.isRequired ? React.createElement(Icon, { data: Lock }) : undefined, hasSelectionIcon, selected: hasSelectionIcon ? tableColumnItem.isSelected : undefined });
    });
};
const prepareValue = (tableColumnItems) => {
    const selectedIds = [];
    tableColumnItems.forEach(({ id, isSelected }) => {
        if (isSelected) {
            selectedIds.push(id);
        }
    });
    return selectedIds;
};
const useDndRenderContainer = ({ onDragEnd, renderControls }) => {
    const uniqId = useUniqId();
    const dndRenderContainer = ({ renderItem, visibleFlattenIds, items: _items, containerRef, id, }) => {
        const visibleFlattenItemList = visibleFlattenIds.map((visibleFlattenId, idx) => renderItem(visibleFlattenId, idx));
        return (React.createElement(React.Fragment, null,
            React.createElement(ListContainerView, { ref: containerRef, id: id },
                React.createElement(DragDropContext, { onDragEnd: onDragEnd },
                    React.createElement(Droppable, { droppableId: uniqId }, (droppableProvided) => {
                        return (React.createElement("div", Object.assign({}, droppableProvided.droppableProps, { ref: droppableProvided.innerRef }),
                            visibleFlattenItemList,
                            droppableProvided.placeholder));
                    }))),
            React.createElement("div", { className: controlsCn }, renderControls())));
    };
    return dndRenderContainer;
};
const useDndRenderItem = (sortable) => {
    const renderDndItem = ({ data, props, index }) => {
        var _a;
        const isDragDisabled = sortable === false;
        const endSlot = (_a = data.endSlot) !== null && _a !== void 0 ? _a : (isDragDisabled ? undefined : React.createElement(Icon, { data: Grip, size: 16 }));
        const commonProps = Object.assign(Object.assign(Object.assign({}, props), data), { endSlot });
        return (React.createElement(Draggable, { draggableId: data.id, index: index, key: `item-key-${data.id}`, isDragDisabled: isDragDisabled }, (provided, snapshot) => {
            const style = Object.assign({}, provided.draggableProps.style);
            // not expected offset appears, one way to fix - remove this offsets explicitly
            if (snapshot.isDragging) {
                style.left = undefined;
                style.top = undefined;
            }
            return (React.createElement(TreeSelectItem, Object.assign({ ref: provided.innerRef }, commonProps, provided.draggableProps, provided.dragHandleProps, { style: style, dragging: snapshot.isDragging })));
        }));
    };
    return renderDndItem;
};
export const TableColumnSetup = (props) => {
    const { renderSwitcher, popupWidth, popupPlacement, items: propsItems, onUpdate: propsOnUpdate, sortable, renderControls, } = props;
    const [open, setOpen] = React.useState(false);
    const [items, setItems] = React.useState(propsItems);
    const [prevPropsItems, setPrevPropsItems] = React.useState(propsItems);
    if (propsItems !== prevPropsItems) {
        setPrevPropsItems(propsItems);
        setItems(propsItems);
    }
    const onApply = () => {
        const newSettings = items.map(({ id, isSelected }) => ({ id, isSelected }));
        propsOnUpdate(newSettings);
        setOpen(false);
    };
    const DefaultApplyButton = () => (React.createElement(Button, { view: "action", width: "max", onClick: onApply }, i18n('button_apply')));
    const onDragEnd = ({ destination, source }) => {
        if ((destination === null || destination === void 0 ? void 0 : destination.index) !== undefined && (destination === null || destination === void 0 ? void 0 : destination.index) !== source.index) {
            setItems((prevItems) => {
                return reorderArray(prevItems, source.index, destination.index);
            });
        }
    };
    const dndRenderContainer = useDndRenderContainer({
        onDragEnd,
        renderControls: () => renderControls ? renderControls({ DefaultApplyButton, onApply }) : React.createElement(DefaultApplyButton, null),
    });
    const dndRenderItem = useDndRenderItem(sortable);
    const renderControl = ({ toggleOpen }) => {
        const onKeyDown = createOnKeyDownHandler(toggleOpen);
        return ((renderSwitcher === null || renderSwitcher === void 0 ? void 0 : renderSwitcher({ onClick: toggleOpen, onKeyDown })) || (React.createElement(Button, { onClick: toggleOpen, extraProps: { onKeyDown } },
            React.createElement(Icon, { data: Gear }),
            i18n('button_switcher'))));
    };
    const onOpenChange = (open) => {
        setOpen(open);
        if (open === false) {
            setItems(propsItems);
        }
    };
    const onUpdate = (selectedItemsIds) => {
        setItems((prevItems) => {
            return prevItems.map((item) => (Object.assign(Object.assign({}, item), { isSelected: selectedItemsIds.includes(item.id) })));
        });
    };
    const [value, dndItems] = React.useMemo(() => [prepareValue(items), prepareDndItems(items)], [items]);
    return (React.createElement(TreeSelect, { className: tableColumnSetupCn, mapItemDataToProps: identity, multiple: true, size: "l", open: open, value: value, items: dndItems, onUpdate: onUpdate, popupWidth: popupWidth, onOpenChange: onOpenChange, placement: popupPlacement, renderContainer: dndRenderContainer, renderControl: renderControl, renderItem: dndRenderItem }));
};
